import { useQueryGetAllBots } from "@apis:bot/bot";
import { useBranding } from "@app-setup:BrandingContext";
import { BrandedLogo } from "@components:BrandedLogo";
import { Header } from "@components:Header";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { BotSelection } from "@modules:BotSelection/BotSelection";
import {
  alpha,
  Box,
  MenuItem,
  Select,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {
  NoScopeState,
  TabErrorState,
} from "@pages:app/analytics/components/AnalyticsStates";
import { DateRangeDropdown } from "@pages:app/analytics/components/DateRangeDropdown";
import {
  ANALYTICS_TABS,
  AnalyticsTabKey,
  isAnalyticsTabKey,
} from "@pages:app/analytics/tabs";
import { useAnalyticsData } from "@pages:app/analytics/tabs/useAnalyticsData";
import {
  getRangeValues,
  getTimezoneAwareDatetime,
  RANGE_TYPE_SLUG,
  RangeType,
} from "@pages:app/analytics/utils";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

export const Analytics = () => {
  const { selectedBotId, setSelectedBotId } = useBotSelection();
  const { setCurrentBotId, getPageConfig, isAllBotsSameOrg } = useBranding();
  const theme = useTheme();
  const { t } = useTranslation();

  const analyticsConfig = getPageConfig("analytics");

  const [orgScope, setOrgScope] = useState(false);

  const { data: allBots } = useQueryGetAllBots(true);
  const selectedBotOrgId =
    allBots?.find((bot) => bot.id === selectedBotId)?.organization_id ?? null;

  // Send exactly one of bot_id / organization_id, matching the backend scope.
  const useOrgScope = orgScope && !!selectedBotOrgId;
  const pageTitle = useOrgScope
    ? t("analytics.orgTitle")
    : analyticsConfig?.title || t("analytics.botTitle");
  const scopeParams = useOrgScope
    ? { organizationId: selectedBotOrgId as string }
    : { botId: selectedBotId ?? undefined };
  const hasScope = useOrgScope || !!selectedBotId;

  // Sync branding context with the selected bot.
  useEffect(() => {
    setCurrentBotId(selectedBotId ?? null);
  }, [selectedBotId, setCurrentBotId]);

  useEffect(() => {
    return () => {
      if (isAllBotsSameOrg) return;
      setCurrentBotId(null);
    };
  }, [setCurrentBotId, isAllBotsSameOrg]);

  // The active feature tab drives request_type; it lives in the URL for deep links.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") ?? "";
  const activeTabKey: AnalyticsTabKey = isAnalyticsTabKey(tabParam)
    ? tabParam
    : "general";
  const activeTab =
    ANALYTICS_TABS.find((tab) => tab.key === activeTabKey) ?? ANALYTICS_TABS[0];

  const handleTabChange = (key: AnalyticsTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const [currentRangeType, setCurrentRangeType] = useState<RangeType>(
    RangeType.LastSevenDays,
  );
  const [range, setRange] = useState<Array<string>>(
    getRangeValues()[currentRangeType] as Array<string>,
  );
  const [tempRange, setTempRange] = useState<{
    fromDate: string | null;
    toDate: string | null;
  }>({
    fromDate: null,
    toDate: null,
  });
  const [isoRange, setIsoRange] = useState<{
    fromDate: string;
    toDate: string;
  }>({
    fromDate: getTimezoneAwareDatetime(range[0], "start"),
    toDate: getTimezoneAwareDatetime(range[1], "end"),
  });

  useEffect(() => {
    if (tempRange.fromDate && tempRange.toDate) {
      setRange([tempRange.fromDate, tempRange.toDate]);
    }
  }, [tempRange]);
  useEffect(() => {
    setIsoRange({
      fromDate: getTimezoneAwareDatetime(range[0], "start"),
      toDate: getTimezoneAwareDatetime(range[1], "end"),
    });
  }, [range]);

  // Button label = preset name or custom range
  const rangeLabel =
    currentRangeType === RangeType.Custom &&
    tempRange.fromDate &&
    tempRange.toDate
      ? `${DateTime.fromISO(tempRange.fromDate).toFormat("LLL d")} – ${DateTime.fromISO(tempRange.toDate).toFormat("LLL d")}`
      : t(`analytics.range.${RANGE_TYPE_SLUG[currentRangeType]}`);

  const handleSelectPreset = (rangeType: RangeType) => {
    setCurrentRangeType(rangeType);
    setRange(getRangeValues()[rangeType] as Array<string>);
    setTempRange({ fromDate: null, toDate: null });
  };

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        minHeight: "100vh",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "30px",
          background: (theme) => theme.palette.common.white,
        }}
      >
        <Header
          sx={{
            background: (theme) => theme.palette.common.white,
            borderTopLeftRadius: "30px",
            borderTopRightRadius: "30px",
          }}
          rightSide={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ paddingX: "0.5rem" }}>
                <BotSelection
                  value={selectedBotId}
                  noMatchText={t("analytics.noBot")}
                  autoSelectBot
                  onChange={setSelectedBotId}
                />
              </Box>
              <SwitchPage />
            </Box>
          }
          leftSide={
            <Box sx={{ marginLeft: "1.5rem" }}>
              <BrandedLogo />
            </Box>
          }
        />
        <Box
          sx={{
            flex: "1 1 0% !important",
            paddingX: "1.5rem",
            paddingY: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.25rem",
          }}
        >
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "bold",
              fontSize: "32px",
              lineHeight: "32px",
              textAlign: "center",
            }}
          >
            {pageTitle}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <Tabs
              value={activeTabKey}
              onChange={(_, key) => handleTabChange(key as AnalyticsTabKey)}
              sx={{ minHeight: "auto" }}
            >
              {ANALYTICS_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={t(`analytics.tabs.${tab.key}`)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    minHeight: "auto",
                    paddingTop: "0.4rem",
                    paddingBottom: "0.4rem",
                    "&:not(.Mui-selected):hover": {
                      color: alpha(theme.palette.primary.main, 0.75),
                    },
                  }}
                />
              ))}
            </Tabs>

            <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <DateRangeDropdown
                label={rangeLabel}
                currentRangeType={currentRangeType}
                fromDate={tempRange.fromDate}
                toDate={tempRange.toDate}
                onSelectPreset={handleSelectPreset}
                onChangeFromDate={(value) => {
                  setCurrentRangeType(RangeType.Custom);
                  setTempRange((prev) => ({
                    ...prev,
                    fromDate: value?.toISODate() as string,
                  }));
                }}
                onChangeToDate={(value) => {
                  setCurrentRangeType(RangeType.Custom);
                  setTempRange((prev) => ({
                    ...prev,
                    toDate: value?.toISODate() as string,
                  }));
                }}
              />

              <Select
                size="small"
                value={orgScope ? "organization" : "bot"}
                onChange={(event) =>
                  setOrgScope(event.target.value === "organization")
                }
                sx={{
                  minWidth: "180px",
                  fontWeight: 600,
                  borderRadius: "7px",
                  color: theme.palette.text.secondary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: "1px",
                  },
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="bot">{t("analytics.scopeBot")}</MenuItem>
                <MenuItem value="organization" disabled={!selectedBotId}>
                  {t("analytics.scopeOrganization")}
                </MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
      </Box>

      <AnalyticsTabRenderer
        key={activeTab.key}
        tab={activeTab}
        scopeParams={scopeParams}
        fromDate={isoRange.fromDate}
        toDate={isoRange.toDate}
        rangeStart={range[0]}
        rangeEnd={range[1]}
        enabled={hasScope}
      />
    </Box>
  );
};

/**
 * Calls the shared data hook and delegates to the tab's layout component.
 */
const AnalyticsTabRenderer = ({
  tab,
  scopeParams,
  fromDate,
  toDate,
  rangeStart,
  rangeEnd,
  enabled,
}: {
  tab: (typeof ANALYTICS_TABS)[number];
  scopeParams: { botId?: string; organizationId?: string };
  fromDate: string;
  toDate: string;
  rangeStart: string;
  rangeEnd: string;
  enabled: boolean;
}) => {
  const data = useAnalyticsData({
    requestType: tab.requestType,
    scopeParams,
    fromDate,
    toDate,
    rangeStart,
    rangeEnd,
    enabled,
    queries: tab.queries,
  });

  if (!enabled) {
    return <NoScopeState />;
  }

  const { activeQueries } = data;

  if (activeQueries.some((q) => q.isError)) {
    return (
      <TabErrorState
        onRetry={() => activeQueries.forEach((q) => q.refetch())}
      />
    );
  }

  const TabComponent = tab.component;
  return <TabComponent data={data} />;
};
